import { scaleBand, scaleOrdinal, scaleLinear } from 'd3-scale'
import { axisBottom } from 'd3-axis'
import { select } from 'd3-selection'
import { keys } from 'd3-collection'
import 'd3-transition'

import { tooltipShow, tooltipHide, emptyElement } from '../utils'

export const panel3Height = 290

export function studentDisadvantageLevel(data, options) {
  const wrap = (textList, width) => {
    textList.each(function () {
      const text = select(this)
      const tmp = `${isInteractive ? 'ⓘ ' : ''}${text.text()}`
      const words = tmp.split(/\s+/)
      let line = []
      let lineNumber = 0
      const lineHeight = 1.1 // ems
      const y = text.attr('y')
      const dy = parseFloat(text.attr('dy')) + 0.5
      let tspan = text
        .text(null)
        .append('tspan')
        .attr('x', 0)
        .attr('y', y)
        .attr('dy', dy + 'em')

      words.forEach((word, index) => {
        line.push(word)
        tspan.text(line.join(' '))
        if (index > 0 && tspan.node().getComputedTextLength() > width) {
          line.pop()
          tspan.text(line.join(' '))
          line = [word]
          tspan = text
            .append('tspan')
            .attr('x', 0)
            .attr('y', y)
            .attr('dy', ++lineNumber * lineHeight + dy + 'em')
            .text(word)
        }
      })
    })
  }

  const { showNational, isInteractive = true } = options
  let group = []
  let disadvantageLevel = {
    data: [
      {
        label: 'College Attendance',
        Neighborhood: data.nh_college_access,
        'High School': data.hs_college_access,
        tip: 'The predicted probability that a student from the neighborhood/high school enrolls in a four-year college',
        group,
      },
      {
        label: 'Household Structure',
        Neighborhood: data.nh_family_ptile,
        'High School': data.hs_family_ptile,
        tip: 'Neighborhood/high school information about the number of married or coupled families, single-parent families, and children living under the poverty line',
        group,
      },
      {
        label: 'Median Family Income',
        Neighborhood: data.nh_medfaminc_ptile,
        'High School': data.hs_medfaminc_ptile,
        tip: 'Median family income among those in the neighborhood/high school',
        group,
      },
      {
        label: 'Housing Stability',
        Neighborhood: data.nh_housing_ptile,
        'High School': data.hs_housing_ptile,
        tip: 'Neighborhood/high school information about vacancy rates, rental vs. home ownership, and mobility/housing turnover',
        group,
      },
      {
        label: 'Educational Level',
        Neighborhood: data.nh_educ_ptile,
        'High School': data.hs_educ_ptile,
        tip: 'Information about the typical educational attainment in the neighborhood/high school.',
        group,
      },
      {
        label: 'Crime',
        Neighborhood: data.nh_crime_ptile,
        'High School': data.hs_crime_ptile,
        tip: 'The predicted probability of being a victim of a crime in the neighborhood or neighborhoods represented by the students attending the high school.',
        group,
      },
    ],
  }

  const container = document.getElementById('panel3Chart2')
  emptyElement(container)

  let margin = { top: 25, right: 0, bottom: 60, left: 0 }
  let width = container.clientWidth
  let height = panel3Height - margin.top - margin.bottom

  let x0 = scaleBand().rangeRound([0, width]).padding(0.1)

  let x1 = scaleBand() // scaleOrdinal()

  let color = scaleOrdinal().range(['rgb(113, 197, 232)', 'rgb(0, 98, 151)']) // '#3972B2'])

  let xAxis = axisBottom(x0)

  let yScale = scaleLinear().range([height, 40]).domain([0, 100])

  let tip
  if (isInteractive) {
    tip = select('#panel3Chart2')
      .append('div')
      .attr('class', 'cb-tooltip')
      .attr('role', 'tooltip')
      .attr('aria-hidden', 'true')
      .style('opacity', '0')
      .style('display', 'none')
  }

  let svg = select('#panel3Chart2')
    .append('svg')
    .attr('class', showNational ? 'chart national' : 'chart')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .attr('aria-hidden', 'true')
    .append('g')
    .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')

  let groupNames = keys(disadvantageLevel.data[0]).filter(function (key) {
    return key !== 'label' && key !== 'group' && key !== 'tip'
  })
  disadvantageLevel.data.forEach(function (d) {
    d.group = groupNames.map(function (name) {
      return { name, value: +d[name] }
    })
  })
  x0.domain(
    disadvantageLevel.data.map(function (d) {
      return d.label
    })
  )
  x1.domain(groupNames).rangeRound([0, x0.bandwidth()])

  svg
    .append('g')
    .attr('class', 'x axis')
    .attr('transform', 'translate(0,' + height + ')')
    .attr('aria-hidden', 'true')
    .call(xAxis)
    .selectAll('.tick text')
    .call(wrap, x0.bandwidth())

  let label = svg
    .selectAll('.label')
    .data(disadvantageLevel.data)
    .enter()
    .append('g')
    .attr('aria-hidden', 'true')
    .attr('class', 'g')
    .attr('transform', function (d) {
      return 'translate(' + x0(d.label) + ',0)'
    })

  label
    .selectAll('rect')
    .data(function (d) {
      return d.group
    })
    .enter()
    .append('rect')
    .attr('width', function (d) {
      return x1.bandwidth()
    })
    .attr('aria-hidden', 'true') //makes width of bars.
    .attr('x', function (d) {
      // makes two bars? I'm not actually sure...
      return x1(d.name)
    })
    .attr('transform', function (d) {
      if (d.value === data.hs_overrall_ptile) {
        return 'translate(0,0)'
      }
    })
    .attr('y', function (d) {
      //makes the bars the right side up
      return yScale(d.value) || 0
    })
    .attr('height', function (d) {
      return height - (yScale(d.value) || 0) //makes the bars height
    })
    .style('fill', function (d) {
      return color(d.name)
    })

  // Top Bar values
  label
    .selectAll('text')
    .data(function (d) {
      return d.group
    })
    .enter()
    .append('text')
    .attr('class', 'barstext')
    .attr('aria-hidden', 'true')
    // .attr('x', function (d) { //aligns the text on top of the right bar
    //   return (x1(d.name) + 5)
    // })
    .attr('x', function (d) {
      return x1(d.name) + x1.bandwidth() / 2
    })
    .attr('text-anchor', 'middle')
    .attr('y', function (d) {
      return (yScale(d.value) || 0) - 10 //places text on top of bars
    })
    .text(function (d) {
      if (d.value) {
        return d.value
      } else {
        return 'NA'
      }
    })
    .attr('class', function (d) {
      return 'context-overview-small-text'
    })
    .attr('font-weight', 900)

  // Legend
  let dataL = 0
  let offset = 100
  let legend = svg
    .selectAll('.legend')
    .data(groupNames.slice())
    .enter()
    .append('g')
    .attr('class', 'legend')
    .attr('aria-hidden', 'true')
    .attr('transform', function (d, i) {
      if (i === 0) {
        let newdataL = dataL
        dataL = d.length + offset
        return 'translate(' + newdataL + ',' + -15 + ')'
      }
      let newdataL = dataL
      dataL += d.length + offset
      return 'translate(' + (newdataL + 15) + ',' + -15 + ')'
    })

  legend
    .append('circle')
    .attr('cy', 6)
    .attr('cx', 30)
    .attr('aria-hidden', 'true')
    .attr('r', 8)
    .style('fill', color)

  legend
    .append('text')
    .attr('dx', function (_, i) {
      if (i === 0) {
        return 120
      }
      return 110
    })
    .attr('dy', '.9em')
    .attr('aria-hidden', 'true')
    .style('text-anchor', 'end')
    .text(function (d) {
      return d
    })

  if (isInteractive) {
    // ToolTip
    svg
      .selectAll('.x .tick')
      .data(disadvantageLevel.data)
      .on('mouseover', function (event, d) {
        //Mouse event
        let xPos = x0(d.label) + (x0.bandwidth() / 2 - 100)
        tooltipShow(tip, d, xPos, 'panel3Chart2')
      })
      .on('mouseout', function (d) {
        //Mouse event
        tooltipHide(tip)
      })
      .on('focus', function (event, d) {
        //Mouse event
        let xPos = x0(d.label) + (x0.bandwidth() / 2 - 100)
        tooltipShow(tip, d, xPos, 'panel3Chart2')
      })
      .on('blur', function (d) {
        //Mouse event
        tooltipHide(tip)
      })
  }
}
